import React, { ChangeEvent, FC, useState } from "react";
import InputAdornment from "@mui/material/InputAdornment";
import TuneIcon from "@mui/icons-material/Tune";
import SearchIcon from "@mui/icons-material/Search";
import { debounce } from "lodash";
import { useTranslation } from "react-i18next";

import {
  StyledSearchContainer,
  StyledIcon,
  StyledMenu,
  StyledMenuItem,
} from "./SearchInput.styled";

import {
  SearchFilter,
  useMultistepFormStore,
} from "@components/common/MultistepForm";
import { TextField } from "@components/base";

const SearchInput: FC = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const {
    searchQuery,
    searchFilter,
    setSearchQuery,
    setSearchFilter,
    formSettings: { editMode },
  } = useMultistepFormStore();
  const { t } = useTranslation();

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = (): void => {
    setAnchorEl(null);
  };

  const handleFilterChange = (filter: SearchFilter): void => {
    setSearchFilter(filter);
    handleMenuClose();
  };

  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery);

  // Debounce logiku pro optimalizaci změny inputu (např. 300ms)
  const debouncedSetSearchQuery = debounce((query: string) => {
    setSearchQuery(query);
  }, 300);

  const handleSearchQueryChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): void => {
    setLocalSearchQuery(event.target.value);
    debouncedSetSearchQuery(event.target.value);
  };

  return (
    <StyledSearchContainer spacing={2}>
      <TextField
        variant="filled"
        size="small"
        value={localSearchQuery}
        onChange={handleSearchQueryChange}
        placeholder={
          searchFilter !== "everything" ? searchFilter : t("common.searchEverything")
        }
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: editMode ? null : (
              <InputAdornment position="end">
                <StyledIcon onClick={handleMenuOpen}>
                  <TuneIcon />
                </StyledIcon>
              </InputAdornment>
            ),
          },
        }}
      />
      <StyledMenu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <StyledMenuItem
          selected={searchFilter === "everything"}
          onClick={() => handleFilterChange("everything")}
        >
          {t("search.everything")}
        </StyledMenuItem>
        <StyledMenuItem
          selected={searchFilter === "answers"}
          onClick={() => handleFilterChange("answers")}
        >
          {t("search.answers")}
        </StyledMenuItem>
        <StyledMenuItem
          selected={searchFilter === "notes"}
          onClick={() => handleFilterChange("notes")}
        >
          {t("search.notes")}
        </StyledMenuItem>
        <StyledMenuItem
          selected={searchFilter === "fileNames"}
          onClick={() => handleFilterChange("fileNames")}
        >
          {t("search.fileNames")}
        </StyledMenuItem>
      </StyledMenu>
    </StyledSearchContainer>
  );
};

export default SearchInput;
